import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCompany } from '../../context/CompanyContext';
import { useToast } from '../../context/ToastContext';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import api, { stripeApi, auth1Api, coldEmailApi } from '../../services/api';
import './CompanyDashboard.css';

const CompanyDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { getCompanyById, selectCompany, exitCompanyView, updateCompanyMetrics } = useCompany();

  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState(null);
  const [authData, setAuthData] = useState(null);
  const [emailData, setEmailData] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);

  const company = getCompanyById(id);

  useEffect(() => {
    if (id) {
      selectCompany(id);
      fetchDashboardData();
    }
  }, [id]);

  const fetchDashboardData = async () => {
    if (!company) return;

    setLoading(true);
    try {
      // Fetch Stripe revenue data for this company
      // Use company ID to fetch company-specific Stripe data if configured
      const stripeUrl = company.stripeSlug
        ? `https://api.z101.ai/api/v1/stripe/${company.stripeSlug}/revenue`
        : `https://api.z101.ai/api/v1/stripe/revenue?company=${id}`;
      const stripeResponse = await fetch(stripeUrl);
      const stripeData = await stripeResponse.json();

      if (stripeData.success) {
        setRevenueData({
          mrr: parseFloat(stripeData.metrics.mrr) || 0,
          arr: parseFloat(stripeData.metrics.arr) || 0,
          growth: 0,
          customers: stripeData.metrics.activeSubscriptions || 0,
          churn: 0,
          totalRevenue: parseFloat(stripeData.metrics.totalRevenue) || 0,
          availableBalance: stripeData.metrics.availableBalance || 0
        });
      }

      // Mock auth data for now (will wire to Auth1 API later)
      setAuthData({
        totalUsers: 1847,
        activeUsers: 1203,
        newUsers: 156,
        sessions: 4521,
        fraudAlerts: 3,
        blockedLogins: 12
      });

      // Mock email data for now (will wire to email API later)
      setEmailData({
        sent: 45200,
        delivered: 44100,
        opened: 18400,
        clicked: 3200,
        bounced: 890,
        complaints: 12
      });

      // Build activity from Stripe charges
      const activityItems = [];
      if (stripeData.recentCharges && stripeData.recentCharges.length > 0) {
        stripeData.recentCharges.forEach((charge, idx) => {
          activityItems.push({
            id: charge.id,
            type: 'payment',
            message: `${charge.status === 'succeeded' ? 'Payment received' : 'Payment ' + charge.status}: $${charge.amount} ${charge.currency.toUpperCase()}`,
            time: new Date(charge.created).toLocaleString(),
            status: charge.status === 'succeeded' ? 'success' : 'warning'
          });
        });
      }

      // Add placeholder activities if no Stripe charges
      if (activityItems.length === 0) {
        activityItems.push(
          { id: 1, type: 'auth', message: 'System ready - waiting for transactions', time: 'Just now', status: 'info' },
          { id: 2, type: 'email', message: 'Email system operational', time: '1 hour ago', status: 'success' }
        );
      }

      setRecentActivity(activityItems);

      // Update company metrics in context
      const mrr = parseFloat(stripeData.metrics?.mrr) || 0;
      updateCompanyMetrics(id, {
        revenue: { mrr: mrr, arr: mrr * 12, growth: 0 },
        users: { total: 1847, active: 1203, new: 156 },
        auth: { logins: 4521, sessions: 4521, fraudAlerts: 3 },
        email: { sent: 45200, delivered: 44100, opened: 18400, clicked: 3200 }
      });

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      showToast('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!company) {
    return (
      <div className="company-dashboard">
        <div className="company-not-found">
          <Icon name="alert-circle" />
          <h2>Company Not Found</h2>
          <p>The company you're looking for doesn't exist.</p>
          <Button variant="primary" onClick={() => navigate('/companies')}>
            Back to Companies
          </Button>
        </div>
      </div>
    );
  }

  const revenueStats = [
    { label: 'MRR', value: `$${(revenueData?.mrr || 0).toLocaleString()}`, change: `+${revenueData?.growth || 0}%`, changeType: 'positive' },
    { label: 'ARR', value: `$${(revenueData?.arr || 0).toLocaleString()}`, change: '-', changeType: 'neutral' },
    { label: 'Customers', value: (revenueData?.customers || 0).toLocaleString(), change: '+12', changeType: 'positive' },
    { label: 'Churn Rate', value: `${revenueData?.churn || 0}%`, change: '-0.3%', changeType: 'positive' },
  ];

  const authStats = [
    { label: 'Total Users', value: (authData?.totalUsers || 0).toLocaleString(), change: `+${authData?.newUsers || 0}`, changeType: 'positive' },
    { label: 'Active Sessions', value: (authData?.sessions || 0).toLocaleString(), change: '-', changeType: 'neutral' },
    { label: 'Fraud Alerts', value: (authData?.fraudAlerts || 0).toString(), change: authData?.fraudAlerts > 0 ? 'Action needed' : 'All clear', changeType: authData?.fraudAlerts > 0 ? 'warning' : 'positive' },
    { label: 'Blocked Logins', value: (authData?.blockedLogins || 0).toString(), change: '-', changeType: 'neutral' },
  ];

  const emailStats = [
    { label: 'Emails Sent', value: (emailData?.sent || 0).toLocaleString(), change: '+2.1K', changeType: 'positive' },
    { label: 'Open Rate', value: `${emailData?.sent ? ((emailData?.opened / emailData?.sent) * 100).toFixed(1) : 0}%`, change: '+1.2%', changeType: 'positive' },
    { label: 'Click Rate', value: `${emailData?.sent ? ((emailData?.clicked / emailData?.sent) * 100).toFixed(1) : 0}%`, change: '+0.5%', changeType: 'positive' },
    { label: 'Bounce Rate', value: `${emailData?.sent ? ((emailData?.bounced / emailData?.sent) * 100).toFixed(1) : 0}%`, change: '-0.2%', changeType: 'positive' },
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'payment': return 'credit-card';
      case 'auth': return 'user';
      case 'email': return 'mail';
      case 'fraud': return 'alert-triangle';
      default: return 'activity';
    }
  };

  return (
    <div className="company-dashboard">
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <button className="back-btn" onClick={() => navigate('/companies')}>
            <Icon name="arrow-left" />
          </button>
          <div
            className="company-logo-large"
            style={{ background: company.color }}
          >
            {company.logo}
          </div>
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">{company.name}</h1>
            <span className="dashboard-domain">{company.domain}</span>
          </div>
        </div>
        <div className="dashboard-header-right">
          <Button variant="secondary" icon="settings" onClick={() => navigate(`/companies/${id}/settings`)}>
            Settings
          </Button>
          <Button variant="secondary" icon="external-link" onClick={() => window.open(`https://${company.domain}`, '_blank')}>
            Visit Site
          </Button>
          <Button variant="primary" icon="refresh" onClick={fetchDashboardData} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Revenue Section */}
      <section className="dashboard-section">
        <div className="section-header">
          <div className="section-title-row">
            <Icon name="credit-card" className="section-icon" />
            <h2 className="section-title">Revenue (Stripe)</h2>
          </div>
          {!company.stripeAccountId && (
            <Button variant="secondary" size="sm" icon="link">
              Connect Stripe
            </Button>
          )}
        </div>
        <div className="stats-row">
          {revenueStats.map((stat, index) => (
            <StatCard key={index} {...stat} loading={loading} />
          ))}
        </div>
      </section>

      {/* Auth Section */}
      <section className="dashboard-section">
        <div className="section-header">
          <div className="section-title-row">
            <Icon name="shield" className="section-icon" />
            <h2 className="section-title">Authentication (Auth1)</h2>
          </div>
          <Button variant="secondary" size="sm" onClick={() => navigate(`/companies/${id}/auth`)}>
            View Details
          </Button>
        </div>
        <div className="stats-row">
          {authStats.map((stat, index) => (
            <StatCard key={index} {...stat} loading={loading} />
          ))}
        </div>
      </section>

      {/* Email Section */}
      <section className="dashboard-section">
        <div className="section-header">
          <div className="section-title-row">
            <Icon name="mail" className="section-icon" />
            <h2 className="section-title">Email Marketing</h2>
          </div>
          <Button variant="secondary" size="sm" onClick={() => navigate('/marketing/email')}>
            View Campaigns
          </Button>
        </div>
        <div className="stats-row">
          {emailStats.map((stat, index) => (
            <StatCard key={index} {...stat} loading={loading} />
          ))}
        </div>
      </section>

      {/* Activity & Quick Actions Grid */}
      <div className="dashboard-grid">
        {/* Recent Activity */}
        <section className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Recent Activity</h2>
            <Button variant="ghost" size="sm">View All</Button>
          </div>
          <div className="activity-list">
            {loading ? (
              <div className="loading-placeholder">Loading...</div>
            ) : recentActivity.length > 0 ? (
              recentActivity.map((item) => (
                <div key={item.id} className={`activity-item ${item.status}`}>
                  <div className={`activity-icon ${item.status}`}>
                    <Icon name={getActivityIcon(item.type)} />
                  </div>
                  <div className="activity-content">
                    <span className="activity-message">{item.message}</span>
                    <span className="activity-time">{item.time}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">No recent activity</div>
            )}
          </div>
        </section>

        {/* Quick Actions */}
        <section className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Quick Actions</h2>
          </div>
          <div className="quick-actions">
            <button className="quick-action" onClick={() => navigate('/marketing/campaigns')}>
              <Icon name="mail" />
              <span>New Email Campaign</span>
            </button>
            <button className="quick-action" onClick={() => navigate('/marketing/leads')}>
              <Icon name="users" />
              <span>Import Leads</span>
            </button>
            <button className="quick-action" onClick={() => navigate(`/companies/${id}/auth`)}>
              <Icon name="shield" />
              <span>View Auth Logs</span>
            </button>
            <button className="quick-action" onClick={() => navigate(`/companies/${id}/revenue`)}>
              <Icon name="bar-chart" />
              <span>Revenue Report</span>
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CompanyDashboard;
